import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  LayoutDashboard, Target, Clock, FileText, Users, BarChart3, Settings,
  Play, CheckCircle, Loader2, ChevronRight, ExternalLink, Activity,
  AlertTriangle, Lightbulb, Zap, TrendingUp, ArrowRight, Calendar,
  Download, Copy, RefreshCw, LogOut, HelpCircle, Sparkles, Rocket,
  Building2, DollarSign, Briefcase, Timer, ListChecks, ArrowUpRight,
  Mail, FileDown, ClipboardList, Megaphone, Wrench, Share2, ArrowLeft,
  Trash2, Pencil, BookOpen, MapPin, Package
} from "lucide-react";

const BOOKING_URL = "https://book.squareup.com/appointments/rg5uyuv09uetad/location/MD083KSF9K29V/services/XHQKFM7EOOEIGVXX724HJPCQ";

// Icon mapping for scaling path
const iconMap = {
  activity: Activity,
  clock: Clock,
  "file-text": FileText,
  target: Target,
  users: Users,
  "bar-chart": BarChart3,
  "clipboard-list": ClipboardList,
  megaphone: Megaphone,
  wrench: Wrench,
  settings: Settings
};

export const ScalingDashboard = ({ user, logout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [profile, setProfile] = useState(null);
  const [scalingPath, setScalingPath] = useState(null);
  const [outputs, setOutputs] = useState({});
  const [sops, setSops] = useState([]);
  const [upsellContext, setUpsellContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [batchExporting, setBatchExporting] = useState(false);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [profileForm, setProfileForm] = useState({
    business_name: "",
    business_type: "service",
    industry: "",
    years_in_business: 1,
    revenue_range: "",
    employee_count: 1,
    has_team: false,
    ceo_hours_per_week: 50,
    ideal_ceo_hours: 25,
    top_time_drains: [],
    biggest_challenges: [],
    ops_maturity: 2,
    has_documented_processes: false,
    has_sops: false,
    has_kpis: false,
    has_org_chart: false,
    has_meeting_rhythm: false,
    pain_points: [],
    primary_goal: "scale",
    timeline_urgency: "90"
  });
  
  // SOP Generator state
  const [showSopGenerator, setShowSopGenerator] = useState(false);
  const [sopForm, setSopForm] = useState({ process_name: "", process_description: "" });
  
  // Program Details Modal
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  
  // Email Output Modal
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipient: "", tool: "", subject: "" });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [profileRes, pathRes, outputsRes, upsellRes] = await Promise.all([
        authFetch(`${API}/scaling/profile`),
        authFetch(`${API}/scaling/recommended-path`),
        authFetch(`${API}/scaling/outputs`),
        authFetch(`${API}/scaling/upsell-context`)
      ]);
      
      if (profileRes.ok) {
        const p = await profileRes.json();
        setProfile(p);
        if (p.business_name) {
          setProfileForm(prev => ({ ...prev, ...p }));
        }
        // Show onboarding if no profile
        if (!p.business_name) {
          setShowOnboarding(true);
        }
      }
      
      if (pathRes.ok) setScalingPath(await pathRes.json());
      if (outputsRes.ok) {
        const data = await outputsRes.json();
        setOutputs(data.outputs || {});
        setSops(data.sops || []);
      }
      if (upsellRes.ok) setUpsellContext(await upsellRes.json());
    } catch (_error) {
      console.error("Error fetching data:", _error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      const res = await authFetch(`${API}/scaling/profile`, {
        method: "POST",
        body: JSON.stringify({ ...profileForm, onboarding_completed: true })
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setShowOnboarding(false);
        toast.success("Profile saved! Let's start scaling.");
        fetchAllData();
      } else {
        toast.error("Failed to save profile");
      }
    } catch (_error) {
      toast.error("Failed to save profile");
    }
  };

  const generateTool = async (tool) => {
    setGenerating(tool);
    try {
      let endpoint = "";
      let body = {};
      
      switch (tool) {
        case "snapshot":
          endpoint = `${API}/scaling/generate/snapshot`;
          break;
        case "time_budget":
          endpoint = `${API}/scaling/generate/time-budget`;
          break;
        case "business_plan":
          endpoint = `${API}/scaling/generate/business-plan`;
          body = { plan_type: "90_day" };
          break;
        case "implementation_plan":
          endpoint = `${API}/scaling/generate/implementation-plan`;
          break;
        case "org_map":
          endpoint = `${API}/scaling/generate/org-map`;
          body = { team_members: [] };
          break;
        case "kpi_scorecard":
          endpoint = `${API}/scaling/generate/kpi-scorecard`;
          body = { current_metrics: {} };
          break;
        case "tech_stack":
          endpoint = `${API}/scaling/generate/tech-stack`;
          break;
        case "marketing_strategy":
          endpoint = `${API}/scaling/generate/marketing-strategy`;
          break;
        case "pricing_strategy":
          endpoint = `${API}/scaling/generate/pricing-strategy`;
          break;
        default:
          toast.error("Tool not available yet");
          setGenerating(null);
          return;
      }
      
      const res = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const data = await res.json();
        setOutputs(prev => ({ ...prev, [tool]: data }));
        toast.success("Generated successfully!");
        fetchAllData(); // Refresh path status
      } else {
        const error = await res.json();
        toast.error(error.detail || "Generation failed");
      }
    } catch (_error) {
      toast.error("Generation failed");
    } finally {
      setGenerating(null);
    }
  };

  const generateSop = async () => {
    if (!sopForm.process_name) {
      toast.error("Process name is required");
      return;
    }
    
    setGenerating("sop");
    try {
      const res = await authFetch(`${API}/scaling/generate/sop`, {
        method: "POST",
        body: JSON.stringify(sopForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setSops(prev => [data, ...prev]);
        setShowSopGenerator(false);
        setSopForm({ process_name: "", process_description: "" });
        toast.success("SOP generated!");
        fetchAllData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Generation failed");
      }
    } catch (_error) {
      toast.error("Generation failed");
    } finally {
      setGenerating(null);
    }
  };

  // Delete SOP
  const deleteSop = async (outputId) => {
    try {
      const res = await authFetch(`${API}/scaling/sops/${outputId}`, { method: "DELETE" });
      if (res.ok) {
        setSops(prev => prev.filter(s => s.output_id !== outputId));
        toast.success("SOP deleted");
      } else {
        toast.error("Failed to delete SOP");
      }
    } catch (_e) {
      toast.error("Delete failed");
    }
  };

  // Update SOP
  const updateSop = async (outputId, updates) => {
    try {
      const res = await authFetch(`${API}/scaling/sops/${outputId}`, {
        method: "PUT",
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const updated = await res.json();
        setSops(prev => prev.map(s => s.output_id === outputId ? updated : s));
        toast.success("SOP updated");
        return true;
      } else {
        toast.error("Failed to update SOP");
        return false;
      }
    } catch (_e) {
      toast.error("Update failed");
      return false;
    }
  };

  // Export output as branded PDF
  const exportOutput = async (tool, output) => {
    try {
      toast.info("Generating branded PDF...");
      const res = await authFetch(`${API}/scaling/export-pdf/${tool}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const toolNames = {
          snapshot: "Business_Health_Snapshot",
          time_budget: "CEO_Time_Budget",
          business_plan: "Business_Plan",
          implementation_plan: "Implementation_Plan",
          org_map: "Org_Map",
          kpi_scorecard: "KPI_Scorecard",
          tech_stack: "Tech_Stack",
          marketing_strategy: "Marketing_Strategy",
          pricing_strategy: "Pricing_Strategy",
          sop: "SOP"
        };
        a.download = `${toolNames[tool] || tool}_MyBizConsulting_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Branded PDF exported!");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed");
    }
  };

  // Batch export all reports as single PDF
  const batchExportAll = async () => {
    setBatchExporting(true);
    try {
      toast.info("Generating comprehensive report... This may take a moment.");
      const res = await authFetch(`${API}/scaling/export-all-pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MyBizConsulting_Full_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Full report exported successfully!");
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to generate batch report");
      }
    } catch (err) {
      console.error("Batch export error:", err);
      toast.error("Batch export failed");
    } finally {
      setBatchExporting(false);
    }
  };

  // Format output for export
  const formatOutputForExport = (tool, output) => {
    let content = `=== ${tool.toUpperCase().replace('_', ' ')} ===\n`;
    content += `Generated: ${output.generated_at || new Date().toISOString()}\n\n`;
    
    if (tool === "snapshot") {
      content += `HEALTH SCORE: ${output.health_score}/100\n\n`;
      content += `TOP 3 BOTTLENECKS:\n`;
      (output.top_3_bottlenecks || output.top_bottlenecks || []).forEach((b, i) => {
        content += `  ${i + 1}. ${b}\n`;
      });
      content += `\nCEO FOCUS AREAS:\n`;
      (output.ceo_focus_areas || []).forEach((f, i) => {
        content += `  ${i + 1}. ${f}\n`;
      });
      content += `\nQUICK WINS:\n`;
      (output.quick_wins || []).forEach((w, i) => {
        content += `  ${i + 1}. ${w}\n`;
      });
      content += `\nRECOMMENDED FIRST ACTION: ${output.recommended_first_action || 'N/A'}\n`;
    } else if (tool === "time_budget") {
      content += `CURRENT HOURS: ${output.current_hours}/week\n`;
      content += `TARGET HOURS: ${output.target_hours}/week\n`;
      content += `HOURS TO RECLAIM: ${output.hours_to_reclaim}\n\n`;
      if (output.delegation_triggers) {
        content += `DELEGATION TRIGGERS:\n`;
        output.delegation_triggers.forEach((d, i) => {
          content += `  ${i + 1}. ${d.task} → ${d.delegate_to} (${d.hours_saved}h saved)\n`;
        });
      }
      if (output.focus_time_rules) {
        content += `\nFOCUS TIME RULES:\n`;
        output.focus_time_rules.forEach((r, i) => {
          content += `  ${i + 1}. ${r}\n`;
        });
      }
    } else if (tool === "business_plan") {
      content += `${output.plan_title || '90-Day Business Plan'}\n\n`;
      content += `EXECUTIVE SUMMARY:\n${output.executive_summary || 'N/A'}\n\n`;
      if (output.strategy_pillars) {
        content += `STRATEGY PILLARS:\n`;
        output.strategy_pillars.forEach((p, i) => {
          content += `  ${i + 1}. ${p.pillar}: ${p.description}\n`;
        });
      }
      if (output.action_plan) {
        content += `\n30/60/90 DAY ACTION PLAN:\n`;
        content += `Day 30:\n`;
        (output.action_plan.day_30 || []).forEach(a => content += `  - ${a}\n`);
        content += `Day 60:\n`;
        (output.action_plan.day_60 || []).forEach(a => content += `  - ${a}\n`);
        content += `Day 90:\n`;
        (output.action_plan.day_90 || []).forEach(a => content += `  - ${a}\n`);
      }
    } else if (tool === "implementation_plan") {
      content += `${output.plan_title || 'Implementation Plan'}\n\n`;
      if (output.ceo_constraints) {
        content += `CEO CONSTRAINTS:\n`;
        content += `  Max Hours/Week: ${output.ceo_constraints.max_hours_per_week}\n`;
        content += `  Off-Limits Tasks: ${(output.ceo_constraints.off_limits_tasks || []).join(', ')}\n\n`;
      }
      if (output.weekly_plan) {
        content += `WEEKLY PLAN:\n`;
        output.weekly_plan.forEach(week => {
          content += `\nWEEK ${week.week}: ${week.theme}\n`;
          content += `  Outcomes: ${(week.outcomes || []).join(', ')}\n`;
          content += `  Tasks:\n`;
          (week.tasks || []).forEach(t => {
            content += `    - ${t.task} (Owner: ${t.owner}, Priority: ${t.priority})\n`;
          });
        });
      }
    } else if (tool === "sop") {
      content += `${output.sop_title || 'SOP'}\n\n`;
      content += `PURPOSE: ${output.purpose || 'N/A'}\n`;
      content += `SCOPE: ${output.scope || 'N/A'}\n`;
      content += `OWNER: ${output.owner || 'N/A'}\n\n`;
      if (output.steps) {
        content += `STEPS:\n`;
        output.steps.forEach(s => {
          content += `  ${s.step_number}. ${s.title}\n`;
          content += `     ${s.description}\n`;
          if (s.time_estimate) content += `     Time: ${s.time_estimate}\n`;
        });
      }
    } else {
      content += JSON.stringify(output, null, 2);
    }
    
    return content;
  };

  // Send output via email
  const sendOutputEmail = async () => {
    if (!emailForm.recipient) {
      toast.error("Recipient email is required");
      return;
    }
    
    setSendingEmail(true);
    try {
      const output = outputs[emailForm.tool];
      const content = formatOutputForExport(emailForm.tool, output);
      
      const res = await authFetch(`${API}/scaling/email-output`, {
        method: "POST",
        body: JSON.stringify({
          recipient: emailForm.recipient,
          subject: emailForm.subject || `Your ${emailForm.tool.replace('_', ' ')} from My Biz Consulting`,
          tool: emailForm.tool,
          content: content
        })
      });
      
      if (res.ok) {
        toast.success(`Email sent to ${emailForm.recipient}`);
        setShowEmailDialog(false);
        setEmailForm({ recipient: "", tool: "", subject: "" });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (_error) {
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailDialog = (tool) => {
    setEmailForm({ 
      recipient: profile?.user_email || "", 
      tool: tool,
      subject: `Your ${tool.replace('_', ' ')} from My Biz Consulting`
    });
    setShowEmailDialog(true);
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" });
    } catch (_e) { /* Logout errors are non-critical */ }
    localStorage.removeItem("auth_token");
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
          <p className="mt-2 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Navigation */}
      <nav className="w-64 bg-white border-r border-gray-200 p-4 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-gray-900">My Biz Consulting</h1>
          <p className="text-sm text-gray-500">Scaling Dashboard</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-3 w-full text-gray-600"
            onClick={() => navigate(user?.role === "consultant" ? "/consultant" : "/dashboard")}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
        
        <div className="space-y-1 flex-1">
          <NavItem icon={LayoutDashboard} label="Home" active={activeTab === "home"} onClick={() => setActiveTab("home")} />
          <NavItem icon={Settings} label="Onboarding" active={activeTab === "onboarding"} onClick={() => { setActiveTab("onboarding"); setShowOnboarding(true); }} />
          <NavItem icon={Rocket} label="Scaling Path" active={activeTab === "path"} onClick={() => setActiveTab("path")} />
          
          <div className="pt-4 pb-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">Tools</p>
          </div>
          
          <NavItem icon={Activity} label="Health Snapshot" active={activeTab === "snapshot"} onClick={() => setActiveTab("snapshot")} />
          <NavItem icon={Clock} label="7-Day Time Budget" active={activeTab === "time_budget"} onClick={() => setActiveTab("time_budget")} />
          <NavItem icon={FileText} label="SOP Generator" active={activeTab === "sops"} onClick={() => setActiveTab("sops")} />
          <NavItem icon={Target} label="Business Plan" active={activeTab === "business_plan"} onClick={() => setActiveTab("business_plan")} />
          <NavItem icon={ClipboardList} label="Implementation Plan" active={activeTab === "implementation_plan"} onClick={() => setActiveTab("implementation_plan")} />
          <NavItem icon={Users} label="Org Map" active={activeTab === "org_map"} onClick={() => setActiveTab("org_map")} />
          <NavItem icon={BarChart3} label="KPIs" active={activeTab === "kpi_scorecard"} onClick={() => setActiveTab("kpi_scorecard")} />
          <NavItem icon={Wrench} label="Tech Stack" active={activeTab === "tech_stack"} onClick={() => setActiveTab("tech_stack")} />
          <NavItem icon={Megaphone} label="Marketing" active={activeTab === "marketing_strategy"} onClick={() => setActiveTab("marketing_strategy")} />
          <NavItem icon={DollarSign} label="Pricing Strategy" active={activeTab === "pricing_strategy"} onClick={() => setActiveTab("pricing_strategy")} />
        </div>
        
        <div className="pt-4 border-t">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name || user?.email}</p>
            <p className="text-xs text-gray-500 truncate">{profile?.business_name || "Complete your profile"}</p>
          </div>
          <NavItem icon={HelpCircle} label="Help & Docs" onClick={() => navigate("/help")} />
          <NavItem icon={LogOut} label="Sign Out" onClick={handleLogout} />
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Upsell Banner */}
        {upsellContext?.show_upsell && (
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-6 py-4">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <div>
                <h3 className="font-semibold">{upsellContext.primary_cta?.headline || "Build your 90-Day Operating System"}</h3>
                <p className="text-sm text-gray-300">{upsellContext.primary_cta?.subheadline || "Done-with-you implementation"}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="text-white border-white hover:bg-white/10" onClick={() => setShowProgramDetails(true)}>
                  What's Included
                </Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => window.open(BOOKING_URL, "_blank")}>
                  Book a Scaling Call
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 max-w-6xl mx-auto">
          {/* Home Tab */}
          {activeTab === "home" && (
            <HomeTab 
              profile={profile}
              scalingPath={scalingPath}
              outputs={outputs}
              upsellContext={upsellContext}
              onGenerateTool={generateTool}
              generating={generating}
              setActiveTab={setActiveTab}
              onBatchExport={batchExportAll}
              batchExporting={batchExporting}
            />
          )}

          {/* Scaling Path Tab */}
          {activeTab === "path" && (
            <ScalingPathTab
              scalingPath={scalingPath}
              outputs={outputs}
              onGenerateTool={generateTool}
              generating={generating}
              setActiveTab={setActiveTab}
            />
          )}

          {/* Tool Tabs */}
          {activeTab === "snapshot" && (
            <ToolTab
              title="Business Health Snapshot"
              description="Get a quick assessment of your business health"
              tool="snapshot"
              output={outputs.snapshot}
              onGenerate={() => generateTool("snapshot")}
              generating={generating === "snapshot"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "time_budget" && (
            <ToolTab
              title="7-Day Time Budget"
              description="Reclaim your time and focus on what matters"
              tool="time_budget"
              output={outputs.time_budget}
              onGenerate={() => generateTool("time_budget")}
              generating={generating === "time_budget"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "sops" && (
            <SOPTab
              sops={sops}
              onShowGenerator={() => setShowSopGenerator(true)}
              onDeleteSop={deleteSop}
              onUpdateSop={updateSop}
              profile={profile}
              onExport={exportOutput}
            />
          )}

          {activeTab === "business_plan" && (
            <ToolTab
              title="90-Day Strategic Plan"
              description="Create your roadmap with clear priorities"
              tool="business_plan"
              output={outputs.business_plan}
              onGenerate={() => generateTool("business_plan")}
              generating={generating === "business_plan"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "implementation_plan" && (
            <ToolTab
              title="Implementation Plan"
              description="Consolidated action plan from all your outputs"
              tool="implementation_plan"
              output={outputs.implementation_plan}
              onGenerate={() => generateTool("implementation_plan")}
              generating={generating === "implementation_plan"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "org_map" && (
            <ToolTab
              title="Org & Delegation Map"
              description="AI-generated organizational structure and delegation opportunities"
              tool="org_map"
              output={outputs.org_map}
              onGenerate={() => generateTool("org_map")}
              generating={generating === "org_map"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "kpi_scorecard" && (
            <ToolTab
              title="KPI Scorecard"
              description="Track the metrics that matter for your business"
              tool="kpi_scorecard"
              output={outputs.kpi_scorecard}
              onGenerate={() => generateTool("kpi_scorecard")}
              generating={generating === "kpi_scorecard"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "tech_stack" && (
            <ToolTab
              title="Tech Stack Recommendations"
              description="Get recommendations for streamlining your business tools"
              tool="tech_stack"
              output={outputs.tech_stack}
              onGenerate={() => generateTool("tech_stack")}
              generating={generating === "tech_stack"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "marketing_strategy" && (
            <ToolTab
              title="Marketing Strategy"
              description="Build your lead generation and conversion strategy"
              tool="marketing_strategy"
              output={outputs.marketing_strategy}
              onGenerate={() => generateTool("marketing_strategy")}
              generating={generating === "marketing_strategy"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}

          {activeTab === "pricing_strategy" && (
            <ToolTab
              title="Pricing Strategy"
              description="Learn the 4 pricing strategies and get a personalized pricing analysis for your business"
              tool="pricing_strategy"
              output={outputs.pricing_strategy}
              onGenerate={() => generateTool("pricing_strategy")}
              generating={generating === "pricing_strategy"}
              profile={profile}
              onExport={exportOutput}
              onEmail={openEmailDialog}
            />
          )}


          {activeTab === "onboarding" && showOnboarding && (
            <OnboardingFlow
              step={onboardingStep}
              setStep={setOnboardingStep}
              form={profileForm}
              setForm={setProfileForm}
              onSave={saveProfile}
              onClose={() => setShowOnboarding(false)}
            />
          )}
        </div>
      </main>

      {/* Onboarding Dialog */}
      <Dialog open={showOnboarding && activeTab !== "onboarding"} onOpenChange={setShowOnboarding}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-600" />
              Let's Build Your Scaling Foundation
            </DialogTitle>
            <DialogDescription>
              Tell us about your business so we can personalize your tools and recommendations.
            </DialogDescription>
          </DialogHeader>
          <OnboardingFlow
            step={onboardingStep}
            setStep={setOnboardingStep}
            form={profileForm}
            setForm={setProfileForm}
            onSave={saveProfile}
            onClose={() => setShowOnboarding(false)}
            isDialog
          />
        </DialogContent>
      </Dialog>

      {/* SOP Generator Dialog */}
      <Dialog open={showSopGenerator} onOpenChange={setShowSopGenerator}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate New SOP</DialogTitle>
            <DialogDescription>
              Describe the process you want to document and we'll create a detailed SOP.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Process Name</Label>
              <Input
                placeholder="e.g., Client Onboarding, Invoice Processing"
                value={sopForm.process_name}
                onChange={(e) => setSopForm(prev => ({ ...prev, process_name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Process Description (Optional)</Label>
              <Textarea
                placeholder="Describe what this process involves..."
                value={sopForm.process_description}
                onChange={(e) => setSopForm(prev => ({ ...prev, process_description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSopGenerator(false)}>Cancel</Button>
            <Button onClick={generateSop} disabled={generating === "sop"} className="bg-green-600 hover:bg-green-700">
              {generating === "sop" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : "Generate SOP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Program Details Modal */}
      <Dialog open={showProgramDetails} onOpenChange={setShowProgramDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>The 90-Day Operating System Buildout</DialogTitle>
            <DialogDescription>
              Move from chaos → clarity → consistent execution
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h4 className="font-semibold mb-3">What we do together:</h4>
              <div className="space-y-3">
                <ProgramPhase title="Weeks 1-2: Clarity & Priorities" description="Align goals, focus areas, and leadership cadence" />
                <ProgramPhase title="Weeks 3-6: Processes & Delegation" description="Build SOPs, assign owners, and create accountability loops" />
                <ProgramPhase title="Weeks 7-10: Metrics & Tools" description="Install KPI scorecards, dashboards, and the right tech stack" />
                <ProgramPhase title="Weeks 11-12: Execution System" description="Weekly operating rhythm + team planning + ongoing improvements" />
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">You'll walk away with:</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> A documented process library (tailored SOPs)</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> A delegation map + clear roles</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> KPI scorecard and review cadence</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Tech stack configured for scale</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> A 90-day plan your team can actually follow</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgramDetails(false)}>Close</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => window.open(BOOKING_URL, "_blank")}>
              Book a Scaling Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Output Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Output</DialogTitle>
            <DialogDescription>
              Send this output to yourself or a client
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Email</Label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={emailForm.recipient}
                onChange={(e) => setEmailForm(prev => ({ ...prev, recipient: e.target.value }))}
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Email subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={sendOutputEmail} disabled={sendingEmail} className="bg-green-600 hover:bg-green-700">
              {sendingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Navigation Item Component
const NavItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    disabled={!!badge}
    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
      active ? "bg-green-50 text-green-700 font-medium" : 
      badge ? "text-gray-400 cursor-not-allowed" : 
      "text-gray-600 hover:bg-gray-100"
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="flex-1 text-left">{label}</span>
    {badge && <Badge variant="outline" className="text-xs">{badge}</Badge>}
  </button>
);

// Program Phase Component
const ProgramPhase = ({ title, description }) => (
  <div className="flex gap-3">
    <div className="w-2 h-2 rounded-full bg-green-600 mt-2 flex-shrink-0" />
    <div>
      <p className="font-medium text-sm">{title}</p>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  </div>
);

// Home Tab Component
const HomeTab = ({ profile, scalingPath, outputs, upsellContext, onGenerateTool, generating, setActiveTab, onBatchExport, batchExporting }) => {
  const snapshot = outputs?.snapshot;
  const completedToolCount = Object.keys(outputs || {}).length;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome back{profile?.business_name ? `, ${profile.business_name}` : ""}!</h1>
          <p className="text-gray-500">Your scaling dashboard and tools</p>
        </div>
        {completedToolCount >= 2 && (
          <Button
            onClick={onBatchExport}
            disabled={batchExporting}
            className="bg-gray-900 hover:bg-gray-800 text-white"
            data-testid="batch-export-all-btn"
          >
            {batchExporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Report...</>
            ) : (
              <><FileDown className="w-4 h-4 mr-2" /> Export All Reports</>
            )}
          </Button>
        )}
      </div>

      {/* Progress Overview */}
      {scalingPath && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold">Your Scaling Progress</h3>
                <p className="text-sm text-gray-500">{scalingPath.progress?.completed} of {scalingPath.progress?.total} foundations complete</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-green-600">{scalingPath.progress?.percentage}%</span>
              </div>
            </div>
            <Progress value={scalingPath.progress?.percentage || 0} className="h-2" />
            
            {scalingPath.next_recommended && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100">
                <p className="text-sm font-medium text-green-800">Next Recommended Step</p>
                <p className="text-sm text-green-700 mt-1">{scalingPath.next_recommended.name}</p>
                <Button 
                  size="sm" 
                  className="mt-3 bg-green-600 hover:bg-green-700"
                  onClick={() => setActiveTab(scalingPath.next_recommended.tool)}
                  disabled={generating}
                >
                  {generating === scalingPath.next_recommended.tool ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <>Start Now <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Snapshot Cards */}
      {snapshot && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  snapshot.health_score >= 70 ? "bg-green-100" : 
                  snapshot.health_score >= 40 ? "bg-yellow-100" : "bg-red-100"
                }`}>
                  <Activity className={`w-6 h-6 ${
                    snapshot.health_score >= 70 ? "text-green-600" : 
                    snapshot.health_score >= 40 ? "text-yellow-600" : "text-red-600"
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Health Score</p>
                  <p className="text-2xl font-bold">{snapshot.health_score}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Top Bottlenecks</p>
              <ul className="space-y-1">
                {(snapshot.top_bottlenecks || []).slice(0, 3).map((b, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 text-amber-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-500 mb-2">CEO Focus Areas</p>
              <ul className="space-y-1">
                {(snapshot.ceo_focus_areas || []).slice(0, 3).map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Target className="w-3 h-3 text-blue-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{f}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Quick Wins</p>
              <ul className="space-y-1">
                {(snapshot.quick_wins || []).slice(0, 3).map((w, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Zap className="w-3 h-3 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{w}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("snapshot")}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">Health Snapshot</p>
              <p className="text-sm text-gray-500">Assess your business</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("time_budget")}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">Time Budget</p>
              <p className="text-sm text-gray-500">Reclaim your time</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab("sops")}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium">SOP Generator</p>
              <p className="text-sm text-gray-500">Document processes</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </CardContent>
        </Card>
      </div>

      {/* Contextual Upsell */}
      {upsellContext?.triggers?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-900">{upsellContext.triggers[0].headline}</h4>
                <p className="text-sm text-amber-800 mt-1">{upsellContext.triggers[0].body}</p>
                <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700" onClick={() => window.open(BOOKING_URL, "_blank")}>
                  {upsellContext.triggers[0].cta}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Scaling Path Tab Component
const ScalingPathTab = ({ scalingPath, outputs, onGenerateTool, generating, setActiveTab }) => {
  if (!scalingPath) return null;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Your Scaling Path</h1>
        <p className="text-gray-500">Complete these steps to get out of day-to-day and scale with structure.</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-gray-500">{scalingPath.progress?.completed} of {scalingPath.progress?.total} complete</span>
            <span className="text-lg font-bold text-green-600">{scalingPath.progress?.percentage}%</span>
          </div>
          <Progress value={scalingPath.progress?.percentage || 0} className="h-2" />
        </CardContent>
      </Card>

      <div className="space-y-4">
        {scalingPath.path?.map((step, index) => {
          const Icon = iconMap[step.icon] || Target;
          const isCompleted = step.status === "completed";
          const isLocked = step.coming_soon;
          
          return (
            <Card key={step.tool} className={isLocked ? "opacity-60" : ""}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? "bg-green-100" : isLocked ? "bg-gray-100" : "bg-blue-100"
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <Icon className={`w-6 h-6 ${isLocked ? "text-gray-400" : "text-blue-600"}`} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{step.name}</h3>
                      {isLocked && <Badge variant="outline">Coming Soon</Badge>}
                      {isCompleted && <Badge className="bg-green-100 text-green-800">Complete</Badge>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      <strong>Why it matters:</strong> {step.why_it_matters}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Est. time: {step.time_estimate}</p>
                  </div>
                  <div>
                    {!isLocked && (
                      <Button
                        onClick={() => isCompleted ? setActiveTab(step.tool) : onGenerateTool(step.tool)}
                        disabled={generating === step.tool}
                        variant={isCompleted ? "outline" : "default"}
                        className={!isCompleted ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {generating === step.tool ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                        ) : isCompleted ? (
                          "View Output"
                        ) : (
                          "Generate Now"
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Completion CTA */}
      {scalingPath.progress?.percentage >= 100 && (
        <Card className="bg-gradient-to-r from-green-600 to-green-700 text-white">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-xl font-bold">Scaling Foundation Complete!</h3>
            <p className="mt-2 opacity-90">Ready for the next level? Let's build your 90-Day Implementation Plan together.</p>
            <Button className="mt-4 bg-white text-green-700 hover:bg-gray-100" onClick={() => window.open(BOOKING_URL, "_blank")}>
              Book Your Implementation Call
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Tool Tab Component
const ToolTab = ({ title, description, tool, output, onGenerate, generating, profile, onExport, onEmail }) => {
  const [showPreview, setShowPreview] = useState(false);
  
  const toolNames = {
    snapshot: "Business Health Snapshot",
    time_budget: "7-Day Time Budget",
    business_plan: "90-Day Strategic Plan",
    implementation_plan: "Implementation Plan",
    org_map: "Org & Delegation Map",
    kpi_scorecard: "KPI Scorecard",
    tech_stack: "Tech Stack Recommendations",
    marketing_strategy: "Marketing Strategy",
    pricing_strategy: "Pricing Strategy"
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-gray-500">{description}</p>
        </div>
        <Button onClick={onGenerate} disabled={generating} className="bg-green-600 hover:bg-green-700">
          {generating ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
          ) : output ? (
            <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate</>
          ) : (
            "Generate Now"
          )}
        </Button>
      </div>

      {/* Profile Context */}
      {profile?.business_name && (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">
              <strong>Using your profile:</strong> {profile.business_name} • {profile.business_type} • {profile.employee_count} employees • {profile.ceo_hours_per_week}hrs/week
            </p>
          </CardContent>
        </Card>
      )}

      {/* Output Display */}
      {output ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Generated Output</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} data-testid="preview-as-client-btn">
                  <ExternalLink className="w-4 h-4 mr-2" /> Preview as Client
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(JSON.stringify(output, null, 2))}>
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
                {onExport && (
                  <Button variant="outline" size="sm" onClick={() => onExport(tool, output)}>
                    <Download className="w-4 h-4 mr-2" /> Export PDF
                  </Button>
                )}
                {onEmail && (
                  <Button variant="outline" size="sm" onClick={() => onEmail(tool)}>
                    <Mail className="w-4 h-4 mr-2" /> Email
                  </Button>
                )}
              </div>
            </div>
            <CardDescription>Generated {output.generated_at ? new Date(output.generated_at).toLocaleDateString() : "recently"}</CardDescription>
          </CardHeader>
          <CardContent>
            <OutputRenderer output={output} tool={tool} />
            <div className="mt-6 pt-4 border-t border-neutral-100 text-center">
              <p className="text-xs text-neutral-400">Powered by My Biz Consulting - Business Solutions Center</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-medium text-gray-900">No output yet</h3>
            <p className="text-sm text-gray-500 mt-1">Click "Generate Now" to create your {title.toLowerCase()}</p>
          </CardContent>
        </Card>
      )}

      {/* Client Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 text-green-600">
              <ExternalLink className="w-5 h-5" />
              <span className="text-sm font-medium">Client Preview</span>
            </div>
            <DialogTitle className="text-xl">{toolNames[tool] || title}</DialogTitle>
            <DialogDescription>
              This is how your client will see this output when shared via email or in their dashboard.
            </DialogDescription>
          </DialogHeader>
          
          {/* Clean client-facing preview */}
          <div className="mt-4 p-6 bg-white border rounded-lg shadow-sm">
            <div className="border-b pb-4 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{toolNames[tool] || title}</h2>
                  <p className="text-sm text-gray-500">
                    Generated for {profile?.business_name || "your business"} • {output?.generated_at ? new Date(output.generated_at).toLocaleDateString() : "Today"}
                  </p>
                </div>
              </div>
            </div>
            
            <OutputRenderer output={output} tool={tool} />
            
            <div className="mt-8 pt-4 border-t text-center">
              <p className="text-xs text-gray-400">
                Powered by My Biz Consulting • Scaling Dashboard
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <p className="text-xs text-gray-500 flex-1">
              Tip: Use "Email" to send this directly to your client
            </p>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close Preview</Button>
            {onEmail && (
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setShowPreview(false); onEmail(tool); }}>
                <Mail className="w-4 h-4 mr-2" /> Send to Client
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Output Renderer Component — Branded & Enhanced
const BrandedHeader = ({ title, subtitle, accentColor = "emerald" }) => (
  <div className="mb-6">
    <div className={`h-1.5 w-full bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-600 rounded-full mb-4`} />
    <div className="flex items-center gap-3 mb-1">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold">My Biz Consulting</p>
        <h3 className="text-lg font-bold text-gray-900 -mt-0.5">{title}</h3>
      </div>
    </div>
    {subtitle && <p className="text-sm text-gray-500 ml-11">{subtitle}</p>}
  </div>
);

const BrandedFooter = () => (
  <div className="mt-8 pt-4 border-t-2 border-emerald-100">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center">
          <Sparkles className="w-3 h-3 text-white" />
        </div>
        <span className="text-xs font-semibold text-emerald-700">My Biz Consulting</span>
      </div>
      <span className="text-[10px] text-gray-400">Business Solutions Center</span>
    </div>
  </div>
);

const MiniBar = ({ value, max, color = "bg-emerald-500", label }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-xs text-gray-500 w-10">{label}</span>}
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-700 w-8 text-right">{value}</span>
    </div>
  );
};

const PRIORITY_COLORS = {
  Critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", bar: "bg-red-500", badge: "bg-red-100 text-red-700 border-red-200" },
  High: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", bar: "bg-amber-500", badge: "bg-amber-100 text-amber-700 border-amber-200" },
  Medium: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", bar: "bg-blue-500", badge: "bg-blue-100 text-blue-700 border-blue-200" },
  Low: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600", bar: "bg-gray-400", badge: "bg-gray-100 text-gray-600 border-gray-200" },
};

const TECH_ICONS = {
  crm: Briefcase, marketing: Megaphone, operations: Settings, finance: DollarSign,
  project_management: ListChecks, communication: Mail, analytics: BarChart3,
  automation: Zap, storage: HelpCircle, security: AlertTriangle,
  default: Rocket
};

const CHANNEL_ICONS = {
  linkedin: Users, email: Mail, content: FileText, social: Share2,
  seo: TrendingUp, referral: Users, podcast: Sparkles, networking: Users,
  default: Megaphone
};

const OutputRenderer = ({ output, tool }) => {
  switch (tool) {
    case "snapshot":
      return (
        <div className="space-y-6">
          <BrandedHeader title="Business Health Snapshot" subtitle="AI-powered assessment of your business" />
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold shadow-lg ${
              output.health_score >= 70 ? "bg-gradient-to-br from-green-400 to-emerald-600 text-white" : 
              output.health_score >= 40 ? "bg-gradient-to-br from-yellow-400 to-amber-500 text-white" : "bg-gradient-to-br from-red-400 to-rose-600 text-white"
            }`}>
              {output.health_score}
            </div>
            <div>
              <p className="font-semibold text-lg">Health Score</p>
              <p className="text-sm text-gray-600">{output.health_summary}</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl border-2 border-amber-100 bg-amber-50/50">
              <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Top Bottlenecks
              </h4>
              <ul className="space-y-2">
                {output.top_bottlenecks?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i+1}</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 rounded-xl border-2 border-blue-100 bg-blue-50/50">
              <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" /> CEO Focus Areas
              </h4>
              <ul className="space-y-2">
                {output.ceo_focus_areas?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i+1}</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="p-4 rounded-xl border-2 border-emerald-100 bg-emerald-50/50">
            <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" /> Quick Wins
            </h4>
            <ul className="space-y-2">
              {output.quick_wins?.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <Zap className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {output.recommended_next_steps?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-emerald-600" /> Recommended Next Steps
              </h4>
              <div className="space-y-2">
                {output.recommended_next_steps.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i+1}</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "time_budget":
      return (
        <div className="space-y-6">
          <BrandedHeader title="CEO Time Budget Analysis" subtitle="Reclaim your most valuable resource" />
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-red-50 to-rose-100 rounded-xl border border-red-200">
              <p className="text-3xl font-bold text-red-600">{output.current_hours}</p>
              <p className="text-xs text-red-500 font-medium">Current hrs/wk</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border border-green-200">
              <p className="text-3xl font-bold text-green-600">{output.target_hours}</p>
              <p className="text-xs text-green-500 font-medium">Target hrs/wk</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl border border-blue-200">
              <p className="text-3xl font-bold text-blue-600">{output.hours_to_reclaim}</p>
              <p className="text-xs text-blue-500 font-medium">Hours to reclaim</p>
            </div>
          </div>

          {output.delegation_opportunities && (
            <div>
              <h4 className="font-semibold mb-3">Delegation Opportunities</h4>
              <div className="space-y-2">
                {output.delegation_opportunities.map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm border border-gray-100">
                    <span className="font-medium">{d.task}</span>
                    <span className="text-gray-500 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> {d.delegate_to}</span>
                    <Badge className="bg-green-100 text-green-700 border-green-200">{d.hours_saved}h saved</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.time_blocking_tips && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-purple-500" /> Time Blocking Tips</h4>
              <ul className="space-y-2">
                {output.time_blocking_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><Clock className="w-4 h-4 text-purple-500 mt-0.5" />{tip}</li>
                ))}
              </ul>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "business_plan":
      return (
        <div className="space-y-6">
          <BrandedHeader title={output.plan_title || "90-Day Strategic Business Plan"} subtitle="Co-developed with My Biz Consulting" />
          
          {output.executive_summary && (
            <div className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-l-4 border-emerald-500">
              <p className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-1">Executive Summary</p>
              <p className="text-gray-700 leading-relaxed">{output.executive_summary}</p>
            </div>
          )}

          {output.market_and_customer && (
            <div className="p-5 bg-white rounded-xl border border-gray-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" /> Market & Customer
              </h4>
              {output.market_and_customer.ideal_customer && <p className="text-sm text-gray-700 mb-2"><strong>Ideal Customer:</strong> {output.market_and_customer.ideal_customer}</p>}
              {output.market_and_customer.market_opportunity && <p className="text-sm text-gray-700 mb-2"><strong>Market Opportunity:</strong> {output.market_and_customer.market_opportunity}</p>}
              {output.market_and_customer.customer_pain_points?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Customer Pain Points:</p>
                  <ul className="space-y-1">{output.market_and_customer.customer_pain_points.map((p, i) => <li key={i} className="text-sm text-gray-600 flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />{p}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {output.offer_and_value_prop && (
            <div className="p-5 bg-white rounded-xl border border-gray-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" /> Offer & Value Proposition
              </h4>
              {output.offer_and_value_prop.core_offer && <p className="text-sm text-gray-700 mb-2"><strong>Core Offer:</strong> {output.offer_and_value_prop.core_offer}</p>}
              {output.offer_and_value_prop.unique_value && <p className="text-sm text-gray-700 mb-2"><strong>Unique Value:</strong> {output.offer_and_value_prop.unique_value}</p>}
              {output.offer_and_value_prop.pricing_model && <p className="text-sm text-gray-700"><strong>Pricing Model:</strong> {output.offer_and_value_prop.pricing_model}</p>}
            </div>
          )}

          {output.strategy_pillars?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" /> Strategy Pillars
              </h4>
              <div className="space-y-3">
                {output.strategy_pillars.map((p, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-gray-200">
                    <div className={`px-4 py-2.5 flex items-center gap-2 ${
                      i === 0 ? "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white" :
                      i === 1 ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white" :
                      i === 2 ? "bg-gradient-to-r from-purple-600 to-purple-500 text-white" :
                      "bg-gradient-to-r from-amber-600 to-amber-500 text-white"
                    }`}>
                      <span className="text-xs font-bold px-2 py-0.5 bg-white/20 rounded">{i + 1}</span>
                      <span className="font-semibold">{p.pillar}</span>
                    </div>
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-700 mb-3">{p.description}</p>
                      {p.key_actions?.length > 0 && (
                        <ul className="space-y-1">{p.key_actions.map((a, j) => <li key={j} className="text-sm text-gray-600 flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />{a}</li>)}</ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.priorities?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" /> Strategic Priorities
              </h4>
              <div className="space-y-3">
                {output.priorities.map((p, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-gray-200">
                    <div className={`px-4 py-2 flex items-center gap-2 ${i === 0 ? "bg-gradient-to-r from-red-500 to-rose-500 text-white" : i === 1 ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white" : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"}`}>
                      <span className="text-xs font-bold px-2 py-0.5 bg-white/20 rounded">P{p.priority}</span>
                      <span className="font-semibold">{p.name}</span>
                    </div>
                    <div className="p-4 bg-white">
                      <p className="text-sm text-gray-700">{p.description}</p>
                      {p.success_metric && (
                        <div className="mt-3 flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                          <p className="text-xs text-emerald-700"><strong>Success Metric:</strong> {p.success_metric}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.operating_plan && (
            <div className="grid md:grid-cols-3 gap-4">
              {output.operating_plan.people?.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <h5 className="font-semibold text-sm text-blue-800 mb-2 flex items-center gap-1"><Users className="w-4 h-4" /> People</h5>
                  <ul className="space-y-1">{output.operating_plan.people.map((p, i) => <li key={i} className="text-xs text-blue-700">{p}</li>)}</ul>
                </div>
              )}
              {output.operating_plan.process?.length > 0 && (
                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <h5 className="font-semibold text-sm text-green-800 mb-2 flex items-center gap-1"><ListChecks className="w-4 h-4" /> Process</h5>
                  <ul className="space-y-1">{output.operating_plan.process.map((p, i) => <li key={i} className="text-xs text-green-700">{p}</li>)}</ul>
                </div>
              )}
              {output.operating_plan.tools?.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <h5 className="font-semibold text-sm text-purple-800 mb-2 flex items-center gap-1"><Wrench className="w-4 h-4" /> Tools</h5>
                  <ul className="space-y-1">{output.operating_plan.tools.map((t, i) => <li key={i} className="text-xs text-purple-700">{t}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {output.kpi_targets?.length > 0 && (
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-600" /> KPI Targets
              </h4>
              <div className="grid md:grid-cols-2 gap-2">
                {output.kpi_targets.map((kpi, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-white rounded-lg border text-sm">
                    <span className="font-medium text-gray-800">{kpi.kpi}</span>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500">{kpi.current}</span>
                      <ArrowRight className="w-3 h-3 text-emerald-500" />
                      <span className="font-semibold text-emerald-700">{kpi.target}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.risks_and_mitigations?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" /> Risks & Mitigations
              </h4>
              <div className="space-y-2">
                {output.risks_and_mitigations.map((r, i) => (
                  <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-sm font-medium text-amber-900">{r.risk}</p>
                    <p className="text-xs text-amber-700 mt-1">Mitigation: {r.mitigation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enhanced 30/60/90 Day Action Plan */}
          {output.action_plan && (
            <div>
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-emerald-600" /> 30/60/90 Day Execution Plan
              </h4>
              {["day_30", "day_60", "day_90"].map((period) => {
                const items = output.action_plan[period];
                if (!items?.length) return null;
                const label = period === "day_30" ? "First 30 Days" : period === "day_60" ? "Days 31-60" : "Days 61-90";
                const color = period === "day_30" ? "emerald" : period === "day_60" ? "blue" : "purple";
                return (
                  <div key={period} className="mb-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-${color}-100 text-${color}-800 font-semibold text-sm mb-3`}>
                      <Calendar className="w-4 h-4" /> {label}
                    </div>
                    <div className="space-y-4">
                      {items.map((item, i) => {
                        const isDetailed = typeof item === "object" && item.action;
                        if (!isDetailed) {
                          return (
                            <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                              <span className={`w-6 h-6 rounded-full bg-${color}-100 text-${color}-700 flex items-center justify-center text-xs font-bold flex-shrink-0`}>{i + 1}</span>
                              <p className="text-sm text-gray-700">{typeof item === "string" ? item : item.action || JSON.stringify(item)}</p>
                            </div>
                          );
                        }
                        return (
                          <div key={i} className={`border border-${color}-200 rounded-xl overflow-hidden`}>
                            <div className={`px-4 py-3 bg-${color}-50 flex items-center justify-between`}>
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-full bg-${color}-200 text-${color}-800 flex items-center justify-center text-xs font-bold`}>{i + 1}</span>
                                <span className="font-semibold text-sm text-gray-900">{item.action}</span>
                              </div>
                              {item.time_investment && <Badge variant="outline" className="text-xs">{item.time_investment}</Badge>}
                            </div>
                            <div className="p-4 bg-white space-y-3">
                              {item.owner && <p className="text-xs text-gray-500">Owner: <strong>{item.owner}</strong></p>}
                              {item.how_to_execute?.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5 text-emerald-600" /> How to Execute:</p>
                                  <ol className="space-y-1.5 ml-1">
                                    {item.how_to_execute.map((step, j) => (
                                      <li key={j} className="text-sm text-gray-700 flex items-start gap-2">
                                        <span className="text-xs font-bold text-emerald-600 mt-0.5 flex-shrink-0">{j + 1}.</span>
                                        {step}
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                              {item.resources?.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-gray-600 mb-1.5 flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-blue-600" /> Resources & Tools:</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {item.resources.map((r, j) => <Badge key={j} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">{r}</Badge>)}
                                  </div>
                                </div>
                              )}
                              {item.expected_outcome && (
                                <div className="flex items-start gap-2 p-2.5 bg-emerald-50 rounded-lg">
                                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-xs font-semibold text-emerald-800">Expected Outcome:</p>
                                    <p className="text-xs text-emerald-700">{item.expected_outcome}</p>
                                  </div>
                                </div>
                              )}
                              {item.success_metric && (
                                <p className="text-xs text-gray-500 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Success Metric: <strong>{item.success_metric}</strong></p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {output.milestones && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" /> Milestones & Timeline
              </h4>
              <div className="relative">
                <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-400 to-green-200" />
                <div className="space-y-4">
                  {output.milestones.map((m, i) => (
                    <div key={i} className="flex gap-4 relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center text-xs font-bold z-10 flex-shrink-0 shadow-md">W{m.week}</div>
                      <div className="flex-1 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                        <p className="font-semibold text-gray-900">{m.milestone}</p>
                        {m.key_actions?.length > 0 && (
                          <ul className="mt-2 space-y-1">{m.key_actions.map((a, j) => <li key={j} className="text-sm text-gray-600 flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />{a}</li>)}</ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {output.kpis_to_track && (
            <div className="p-4 bg-gray-50 rounded-xl">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-emerald-600" /> KPIs to Track
              </h4>
              <div className="flex flex-wrap gap-2">
                {output.kpis_to_track.map((kpi, i) => <Badge key={i} className="bg-emerald-100 text-emerald-700 border border-emerald-200">{kpi}</Badge>)}
              </div>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "org_map":
      return (
        <div className="space-y-6">
          <BrandedHeader title={output.org_title || "Organizational Map"} subtitle="Optimized structure for growth" />

          {output.current_state && (
            <div className="p-5 rounded-xl bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-600" /> Current State
              </h4>
              <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
                {output.current_state.structure_type && (
                  <div className="flex gap-2"><Badge variant="outline">Structure:</Badge> <span>{output.current_state.structure_type}</span></div>
                )}
                {output.current_state.team_size && (
                  <div className="flex gap-2"><Badge variant="outline">Team:</Badge> <span>{output.current_state.team_size}</span></div>
                )}
              </div>
              {output.current_state.key_gaps?.length > 0 && (
                <ul className="space-y-1">
                  {output.current_state.key_gaps.map((gap, i) => (
                    <li key={i} className="text-sm flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />{gap}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Visual Org Chart */}
          {output.recommended_structure?.org_chart && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" /> Recommended Org Chart
              </h4>
              <OrgChartVisual roles={output.recommended_structure.org_chart} />
            </div>
          )}

          {output.recommended_structure?.critical_hires && (
            <div>
              <h4 className="font-semibold mb-3">Critical Hires</h4>
              <div className="space-y-2">
                {output.recommended_structure.critical_hires.map((hire, i) => (
                  <div key={i} className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-sm flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" />{hire.role}</span>
                      <Badge className={hire.priority === "High" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{hire.priority} Priority</Badge>
                    </div>
                    <p className="text-sm text-gray-600">{hire.why_needed}</p>
                    <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {hire.when_to_hire}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.delegation_matrix && (
            <div>
              <h4 className="font-semibold mb-3">Delegation Matrix</h4>
              <div className="space-y-2">
                {output.delegation_matrix.map((item, i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between text-sm border border-gray-100">
                    <div>
                      <p className="font-medium">{item.task_category}</p>
                      <p className="text-gray-500 text-xs flex items-center gap-1">{item.current_owner} <ArrowRight className="w-3 h-3" /> {item.recommended_owner}</p>
                    </div>
                    <Badge variant="outline">{item.delegation_priority}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.ceo_focus_shift && (
            <div className="grid md:grid-cols-3 gap-3">
              <div className="p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200">
                <h5 className="font-semibold text-red-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Stop Doing</h5>
                <ul className="space-y-1 text-sm">{output.ceo_focus_shift.stop_doing?.map((item, i) => <li key={i} className="text-gray-700">&#8226; {item}</li>)}</ul>
              </div>
              <div className="p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-amber-200">
                <h5 className="font-semibold text-amber-700 mb-2 flex items-center gap-1"><Clock className="w-4 h-4" /> Keep Doing</h5>
                <ul className="space-y-1 text-sm">{output.ceo_focus_shift.keep_doing?.map((item, i) => <li key={i} className="text-gray-700">&#8226; {item}</li>)}</ul>
              </div>
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <h5 className="font-semibold text-green-700 mb-2 flex items-center gap-1"><Rocket className="w-4 h-4" /> Start Doing</h5>
                <ul className="space-y-1 text-sm">{output.ceo_focus_shift.start_doing?.map((item, i) => <li key={i} className="text-gray-700">&#8226; {item}</li>)}</ul>
              </div>
            </div>
          )}

          {output.implementation_steps && (
            <div>
              <h4 className="font-semibold mb-2">Implementation Steps</h4>
              <ol className="space-y-2">
                {output.implementation_steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "kpi_scorecard":
      return (
        <div className="space-y-6">
          <BrandedHeader title={output.scorecard_title || "KPI Scorecard"} subtitle="Performance metrics & tracking dashboard" />

          {(output.business_health_kpis || output.kpi_categories) && (
            <div className="space-y-3">
              <h4 className="font-semibold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-600" /> Key Performance Indicators</h4>
              {Array.isArray(output.business_health_kpis) ? (
                <div className="grid md:grid-cols-2 gap-3">
                  {output.business_health_kpis.map((kpi, i) => {
                    const pColors = PRIORITY_COLORS[kpi.priority] || PRIORITY_COLORS.Medium;
                    return (
                      <div key={i} className={`rounded-xl overflow-hidden border-2 ${pColors.border}`}>
                        <div className={`${pColors.bg} px-4 py-3`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{kpi.kpi_name || kpi.name}</span>
                            <Badge className={pColors.badge}>{kpi.priority || "Medium"}</Badge>
                          </div>
                          {kpi.category && <span className="text-xs text-gray-500">{kpi.category}</span>}
                        </div>
                        <div className="p-4 bg-white space-y-2">
                          {kpi.description && <p className="text-sm text-gray-600">{kpi.description}</p>}
                          {kpi.formula && (
                            <div className="p-2 bg-gray-50 rounded-lg border border-gray-100">
                              <p className="text-xs text-gray-500 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Formula</p>
                              <p className="text-xs font-mono text-gray-700">{kpi.formula}</p>
                            </div>
                          )}
                          {(kpi.target_30_day || kpi.target_90_day) && (
                            <div className="space-y-1.5 pt-1">
                              {kpi.target_30_day && <MiniBar value={30} max={90} color={pColors.bar} label="30d" />}
                              {kpi.target_90_day && <MiniBar value={90} max={90} color="bg-emerald-500" label="90d" />}
                              <div className="flex justify-between text-[10px] text-gray-400 px-12">
                                <span>{kpi.target_30_day}</span>
                                <span>{kpi.target_90_day}</span>
                              </div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            {kpi.tracking_frequency && <Badge variant="outline" className="text-[10px]"><Clock className="w-3 h-3 mr-1" />{kpi.tracking_frequency}</Badge>}
                            {kpi.owner && <Badge variant="outline" className="text-[10px]"><Users className="w-3 h-3 mr-1" />{kpi.owner}</Badge>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : Array.isArray(output.kpi_categories) && output.kpi_categories.map((cat, i) => (
                <div key={i} className="border-2 rounded-xl overflow-hidden border-emerald-200">
                  <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-green-50 font-semibold text-emerald-800">{cat.category}</div>
                  <div className="p-4 space-y-3">
                    {cat.kpis?.map((kpi, j) => (
                      <div key={j} className="p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium text-sm">{kpi.name}</span>
                        {kpi.description && <p className="text-sm text-gray-600">{kpi.description}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(output.leading_indicators) && output.leading_indicators.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" /> Leading Indicators</h4>
              <div className="space-y-2">
                {output.leading_indicators.map((item, i) => (
                  <div key={i} className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 text-sm">
                    <p className="font-semibold text-blue-800">{item.indicator}</p>
                    {item.what_it_predicts && <p className="text-gray-600 mt-1">Predicts: {item.what_it_predicts}</p>}
                    {item.action_threshold && <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Threshold: {item.action_threshold}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.dashboard_structure && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><LayoutDashboard className="w-4 h-4 text-emerald-600" /> Dashboard Structure</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {Array.isArray(output.dashboard_structure) ? (
                  output.dashboard_structure.map((section, i) => (
                    <div key={i} className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                      <p className="font-semibold text-sm text-emerald-700 mb-2">{section.section}</p>
                      <ul className="text-xs text-gray-600 space-y-1">{section.metrics?.map((m, j) => <li key={j} className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />{m}</li>)}</ul>
                    </div>
                  ))
                ) : (
                  Object.entries(output.dashboard_structure).map(([key, metrics], i) => (
                    <div key={i} className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                      <p className="font-semibold text-sm text-emerald-700 mb-2 capitalize">{key.replace(/_/g, " ")}</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {Array.isArray(metrics) ? metrics.map((m, j) => <li key={j} className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />{m}</li>) : <li>{String(metrics)}</li>}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {(output.meeting_cadence || output.tracking_cadence) && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-emerald-600" /> Review Cadence</h4>
              <div className="space-y-2">
                {(Array.isArray(output.meeting_cadence) ? output.meeting_cadence : Array.isArray(output.tracking_cadence) ? output.tracking_cadence : []).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100 text-sm">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">{item.frequency || item.meeting}:</span>
                    <span className="text-gray-600">{item.activity || item.purpose || item.agenda}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(output.quick_wins) && output.quick_wins.length > 0 && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <h4 className="font-semibold mb-2 text-green-800 flex items-center gap-2"><Zap className="w-4 h-4" /> Quick Wins</h4>
              <ul className="space-y-2">
                {output.quick_wins.map((win, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700"><Zap className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{typeof win === "string" ? win : win.action || JSON.stringify(win)}</li>
                ))}
              </ul>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "tech_stack":
      return (
        <div className="space-y-6">
          <BrandedHeader title={output.recommendation_title || output.stack_title || "Tech Stack Recommendations"} subtitle="Future-proof your business technology" />

          {output.current_assessment && (
            <div className="p-5 rounded-xl bg-gradient-to-br from-slate-900 to-gray-800 text-white">
              <h4 className="font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-cyan-400" /> Current Assessment</h4>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {output.current_assessment.tool_count_verdict && (
                  <div className="text-center p-3 bg-white/10 rounded-lg backdrop-blur">
                    <Wrench className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-300">Tools</p>
                    <p className="text-sm font-bold text-white">{output.current_assessment.tool_count_verdict}</p>
                  </div>
                )}
                {output.current_assessment.integration_score && (
                  <div className="text-center p-3 bg-white/10 rounded-lg backdrop-blur">
                    <Share2 className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-300">Integration</p>
                    <p className="text-sm font-bold text-white">{output.current_assessment.integration_score}</p>
                  </div>
                )}
                {output.current_assessment.cost_efficiency && (
                  <div className="text-center p-3 bg-white/10 rounded-lg backdrop-blur">
                    <DollarSign className="w-5 h-5 text-green-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-300">Cost</p>
                    <p className="text-sm font-bold text-white">{output.current_assessment.cost_efficiency}</p>
                  </div>
                )}
              </div>
              {Array.isArray(output.current_assessment.key_issues) && (
                <ul className="space-y-1">
                  {output.current_assessment.key_issues.map((item, i) => (
                    <li key={i} className="text-sm flex items-start gap-2 text-gray-300"><AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />{item}</li>
                  ))}
                </ul>
              )}
              {Array.isArray(output.current_assessment.strengths) && output.current_assessment.strengths.length > 0 && (
                <ul className="space-y-1 mt-2">
                  {output.current_assessment.strengths.map((item, i) => (
                    <li key={i} className="text-sm flex items-start gap-2 text-gray-300"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />{item}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {output.recommended_stack && typeof output.recommended_stack === "object" && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2"><Rocket className="w-5 h-5 text-cyan-600" /> Recommended Stack</h4>
              <div className="space-y-4">
                {Object.entries(output.recommended_stack).map(([category, tools], i) => {
                  const CatIcon = TECH_ICONS[category] || TECH_ICONS.default;
                  return Array.isArray(tools) && tools.length > 0 && (
                    <div key={i} className="rounded-xl overflow-hidden border-2 border-gray-200">
                      <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-gray-700 flex items-center gap-2">
                        <CatIcon className="w-5 h-5 text-cyan-400" />
                        <span className="font-semibold text-white capitalize">{category.replace(/_/g, " ")}</span>
                      </div>
                      <div className="p-3 space-y-2 bg-white">
                        {tools.map((t, j) => (
                          <div key={j} className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                                <CatIcon className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="font-semibold text-sm">{t.tool || t.category}</p>
                                {t.why && <p className="text-xs text-gray-500">{t.why}</p>}
                              </div>
                            </div>
                            {t.cost_range && <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200">{t.cost_range}</Badge>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(output.recommendations) && output.recommendations.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Recommendations</h4>
              <div className="space-y-3">
                {output.recommendations.map((rec, i) => (
                  <div key={i} className="p-4 border-2 rounded-xl border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold flex items-center gap-2"><Rocket className="w-4 h-4 text-cyan-600" />{rec.category}</span>
                      {rec.priority && <Badge className={PRIORITY_COLORS[rec.priority]?.badge || "bg-gray-100"}>{rec.priority}</Badge>}
                    </div>
                    {rec.current_tool && <p className="text-sm text-gray-500">Current: {rec.current_tool}</p>}
                    {rec.recommended_tool && <p className="text-sm text-cyan-700 font-semibold flex items-center gap-1"><ArrowRight className="w-3 h-3" /> {rec.recommended_tool}</p>}
                    {rec.reason && <p className="text-sm text-gray-600 mt-2">{rec.reason}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(output.tools_to_eliminate) && output.tools_to_eliminate.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" /> Tools to Eliminate</h4>
              <div className="space-y-2">
                {output.tools_to_eliminate.map((item, i) => (
                  <div key={i} className="p-3 bg-red-50 rounded-xl border border-red-200 text-sm">
                    <p className="font-semibold text-red-700">{item.tool}</p>
                    {item.reason && <p className="text-gray-600">{item.reason}</p>}
                    {item.replacement && <p className="text-emerald-600 text-xs mt-1 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Replace with: {item.replacement}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(output.integration_recommendations) && output.integration_recommendations.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2"><Share2 className="w-4 h-4 text-purple-600" /> Integration Tips</h4>
              <ul className="space-y-2">
                {output.integration_recommendations.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm"><Wrench className="w-4 h-4 text-purple-500 mt-0.5" />{typeof tip === "string" ? tip : tip.action || JSON.stringify(tip)}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(output.implementation_priority) && output.implementation_priority.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-600" /> Implementation Priority</h4>
              <div className="space-y-2">
                {output.implementation_priority.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-100 text-sm">
                    <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <div>
                      <span className="font-semibold">{step.action || (typeof step === "string" ? step : "")}</span>
                      {step.timeline && <span className="text-gray-500 ml-2">({step.timeline})</span>}
                      {step.impact && <Badge className="ml-2 bg-green-100 text-green-700">{step.impact}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {output.estimated_monthly_savings && (
            <div className="p-5 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl text-white text-center">
              <p className="text-sm text-white/80">Estimated Monthly Savings</p>
              <p className="text-3xl font-bold mt-1">{output.estimated_monthly_savings}</p>
            </div>
          )}

          {Array.isArray(output.quick_wins) && output.quick_wins.length > 0 && (
            <div className="p-4 rounded-xl bg-cyan-50 border border-cyan-200">
              <h4 className="font-semibold mb-2 text-cyan-800 flex items-center gap-2"><Zap className="w-4 h-4" /> Quick Wins</h4>
              <ul className="space-y-2">
                {output.quick_wins.map((win, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700"><Zap className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />{typeof win === "string" ? win : win.action || JSON.stringify(win)}</li>
                ))}
              </ul>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    case "marketing_strategy":
      return (
        <div className="space-y-6">
          <BrandedHeader title={output.strategy_title || "Marketing Strategy"} subtitle="Data-driven growth playbook" />

          {output.target_audience && (
            <div className="p-5 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
              <h4 className="font-semibold mb-3 flex items-center gap-2 text-purple-800"><Target className="w-5 h-5" /> Target Audience</h4>
              {(output.target_audience.ideal_customer_profile || output.target_audience.primary) && (
                <div className="p-3 bg-white rounded-lg mb-3 border border-purple-100">
                  <p className="text-xs text-purple-600 font-semibold mb-1">Ideal Customer Profile</p>
                  <p className="text-sm text-gray-700">{output.target_audience.ideal_customer_profile || output.target_audience.primary}</p>
                </div>
              )}
              {Array.isArray(output.target_audience.pain_points_addressed || output.target_audience.pain_points) && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-purple-600 mb-1">Pain Points</p>
                  <div className="flex flex-wrap gap-2">
                    {(output.target_audience.pain_points_addressed || output.target_audience.pain_points || []).map((point, i) => (
                      <Badge key={i} className="bg-purple-100 text-purple-700 border-purple-200">{point}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(output.target_audience.where_they_hang_out) && (
                <div>
                  <p className="text-xs font-semibold text-purple-600 mb-1">Where to Find Them</p>
                  <div className="flex flex-wrap gap-2">
                    {output.target_audience.where_they_hang_out.map((place, i) => (
                      <Badge key={i} variant="outline" className="border-purple-300 text-purple-600">{place}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {Array.isArray(output.marketing_channels || output.channels) && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2"><Megaphone className="w-5 h-5 text-rose-600" /> Marketing Channels</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {(output.marketing_channels || output.channels).map((channel, i) => {
                  const channelName = (channel.channel || channel.name || "").toLowerCase();
                  const CIcon = Object.entries(CHANNEL_ICONS).find(([k]) => channelName.includes(k))?.[1] || CHANNEL_ICONS.default;
                  const colors = ["border-blue-300 bg-blue-50", "border-rose-300 bg-rose-50", "border-purple-300 bg-purple-50", "border-amber-300 bg-amber-50", "border-green-300 bg-green-50", "border-indigo-300 bg-indigo-50"];
                  return (
                    <div key={i} className={`rounded-xl overflow-hidden border-2 ${colors[i % colors.length]}`}>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm flex items-center gap-2"><CIcon className="w-4 h-4" />{channel.channel || channel.name}</span>
                          {channel.priority && <Badge className={PRIORITY_COLORS[channel.priority]?.badge || "bg-gray-100"}>{channel.priority}</Badge>}
                        </div>
                        {(channel.why || channel.strategy) && <p className="text-sm text-gray-600 mb-2">{channel.why || channel.strategy}</p>}
                        {Array.isArray(channel.tactics) && channel.tactics.length > 0 && (
                          <ul className="text-xs text-gray-600 space-y-1">
                            {channel.tactics.map((t, j) => <li key={j} className="flex items-start gap-1"><ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />{t}</li>)}
                          </ul>
                        )}
                        {(channel.time_investment || channel.frequency) && (
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {channel.time_investment || channel.frequency}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(output.content_strategy || output.content_pillars) && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" /> Content Strategy</h4>
              {Array.isArray(output.content_strategy?.content_pillars || output.content_pillars) && (
                <div className="grid md:grid-cols-2 gap-3">
                  {(output.content_strategy?.content_pillars || output.content_pillars).map((pillar, i) => {
                    const pillColors = ["bg-indigo-50 border-indigo-200", "bg-violet-50 border-violet-200", "bg-pink-50 border-pink-200", "bg-cyan-50 border-cyan-200"];
                    return (
                      <div key={i} className={`p-4 rounded-xl border ${pillColors[i % pillColors.length]}`}>
                        <p className="font-semibold text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4" />{typeof pillar === "string" ? pillar : pillar.pillar || pillar.name}</p>
                        {pillar.topics?.length > 0 && (
                          <ul className="mt-2 text-xs text-gray-600 space-y-1">{pillar.topics.map((t, j) => <li key={j} className="flex items-start gap-1"><ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />{t}</li>)}</ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {Array.isArray(output.content_strategy?.content_types) && (
                <div className="mt-3 space-y-2">
                  {output.content_strategy.content_types.map((ct, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                      <FileText className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                      <span className="font-medium">{ct.type}</span>
                      {ct.frequency && <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-xs">{ct.frequency}</Badge>}
                      {ct.purpose && <span className="text-gray-500">{ct.purpose}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {output.lead_generation && (
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> Lead Generation</h4>
              {Array.isArray(output.lead_generation) ? (
                <div className="space-y-2">
                  {output.lead_generation.map((item, i) => (
                    <div key={i} className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                      <p className="font-semibold text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-green-600" />{item.tactic || item.name}</p>
                      {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {Array.isArray(output.lead_generation.lead_magnets) && (
                    <div>
                      <p className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1"><Sparkles className="w-4 h-4" /> Lead Magnets</p>
                      <div className="grid md:grid-cols-2 gap-2">
                        {output.lead_generation.lead_magnets.map((mag, i) => (
                          <div key={i} className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                            <p className="font-semibold text-sm">{mag.name}</p>
                            {mag.type && <Badge variant="outline" className="text-xs mt-1">{mag.type}</Badge>}
                            {mag.target_conversion && <p className="text-xs text-green-600 mt-1 font-semibold">{mag.target_conversion}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {Array.isArray(output.lead_generation.nurture_sequence) && (
                    <div>
                      <p className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1"><Mail className="w-4 h-4" /> Nurture Sequence</p>
                      <div className="relative">
                        <div className="absolute left-[14px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 to-blue-200" />
                        {output.lead_generation.nurture_sequence.map((step, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm pb-3 relative">
                            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 z-10">{i + 1}</span>
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 flex-1">
                              <p className="font-semibold">{step.stage}</p>
                              {step.touchpoint && <p className="text-gray-600">{step.touchpoint}</p>}
                              {step.goal && <p className="text-xs text-blue-600 mt-1">{step.goal}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {Array.isArray(output["90_day_priorities"]) && output["90_day_priorities"].length > 0 && (
            <div className="p-4 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200">
              <h4 className="font-semibold mb-3 text-purple-800 flex items-center gap-2"><Calendar className="w-4 h-4" /> 90-Day Priorities</h4>
              <ol className="space-y-2">
                {output["90_day_priorities"].map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-purple-200 text-purple-800 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    <span className="text-gray-700">{p}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {Array.isArray(output.quick_wins) && output.quick_wins.length > 0 && (
            <div className="p-4 rounded-xl bg-green-50 border border-green-200">
              <h4 className="font-semibold mb-2 text-green-800 flex items-center gap-2"><Zap className="w-4 h-4" /> Quick Wins</h4>
              <ul className="space-y-2">
                {output.quick_wins.map((win, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700"><Zap className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{typeof win === "string" ? win : win.action || JSON.stringify(win)}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(output.metrics_to_track) && output.metrics_to_track.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-600" /> Metrics to Track</h4>
              <div className="flex flex-wrap gap-2">
                {output.metrics_to_track.map((m, i) => (
                  <Badge key={i} className="bg-emerald-100 text-emerald-700 border-emerald-200">{m}</Badge>
                ))}
              </div>
            </div>
          )}
          <BrandedFooter />
        </div>
      );


    case "pricing_strategy":
      return (
        <div className="space-y-6">
          <BrandedHeader title="Pricing Strategy Analysis" subtitle="Setting Smart Prices for Your Business" />

          {/* Pricing Lesson */}
          {output.pricing_lesson && (
            <>
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-sm text-emerald-800">{output.pricing_lesson.introduction}</p>
              </div>

              {output.pricing_lesson.strategies?.map((strat, i) => (
                <div key={i} className="p-5 rounded-xl border-2 border-neutral-200 bg-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#39FF14]/20 flex items-center justify-center text-sm font-bold text-[#2BC40E]">{i + 1}</div>
                    <h4 className="font-bold text-neutral-900">{strat.name}</h4>
                  </div>
                  <p className="text-sm text-neutral-700 mb-3">{strat.definition}</p>
                  <div className="grid md:grid-cols-2 gap-4 mb-3">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-1">How It Works</p>
                      <p className="text-sm text-blue-900">{strat.how_it_works}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs font-semibold text-purple-700 mb-1">Best For</p>
                      <p className="text-sm text-purple-900">{strat.best_for}</p>
                    </div>
                  </div>
                  {strat.formula && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-3">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Formula</p>
                      <p className="text-sm font-mono text-amber-900">{strat.formula}</p>
                    </div>
                  )}
                  {strat.example && (
                    <div className="p-3 bg-neutral-50 rounded-lg mb-3">
                      <p className="text-xs font-semibold text-neutral-600 mb-1">Example</p>
                      <p className="text-sm text-neutral-700">{strat.example}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-semibold text-green-700 mb-1">Pros</p>
                      <ul className="space-y-1">{strat.pros?.map((p, j) => <li key={j} className="text-xs text-green-800 flex items-start gap-1"><CheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />{p}</li>)}</ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-700 mb-1">Cons</p>
                      <ul className="space-y-1">{strat.cons?.map((c, j) => <li key={j} className="text-xs text-red-800 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 text-red-400 flex-shrink-0" />{c}</li>)}</ul>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Personalized Recommendation */}
          {output.business_analysis && (
            <div className="p-5 rounded-xl bg-gradient-to-r from-[#39FF14]/10 to-emerald-50 border-2 border-[#39FF14]/30">
              <h4 className="font-bold text-neutral-900 mb-3 flex items-center gap-2"><Star className="w-5 h-5 text-[#39FF14]" /> Your Recommended Strategy</h4>
              <div className="p-4 bg-white rounded-lg border border-[#39FF14]/20 mb-3">
                <p className="font-semibold text-[#2BC40E] mb-1">{output.business_analysis.recommended_strategy}</p>
                <p className="text-sm text-neutral-700">{output.business_analysis.reasoning}</p>
              </div>
              {output.business_analysis.secondary_strategy && (
                <div className="p-3 bg-white/50 rounded-lg">
                  <p className="text-xs font-semibold text-neutral-600 mb-1">Complementary Strategy</p>
                  <p className="text-sm text-neutral-700">{output.business_analysis.secondary_strategy}</p>
                </div>
              )}
            </div>
          )}

          {/* Economics Per Unit */}
          {output.economics_per_unit && (
            <div className="p-5 rounded-xl bg-blue-50 border border-blue-200">
              <h4 className="font-semibold mb-3 text-blue-800">Economics Per Unit</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-white rounded-lg"><p className="text-xs text-blue-600">Est. Sales Price</p><p className="font-bold text-blue-900">{output.economics_per_unit.estimated_sales_price}</p></div>
                <div className="p-3 bg-white rounded-lg"><p className="text-xs text-blue-600">Target GPM Range</p><p className="font-bold text-blue-900">{output.economics_per_unit.target_gpm_range}</p></div>
                <div className="p-3 bg-white rounded-lg"><p className="text-xs text-blue-600">Markup Recommendation</p><p className="font-bold text-blue-900">{output.economics_per_unit.markup_recommendation}</p></div>
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-xs text-blue-600">Typical COS Items</p>
                  <ul className="text-xs text-blue-800">{output.economics_per_unit.typical_cos_breakdown?.map((c, i) => <li key={i}>• {c}</li>)}</ul>
                </div>
              </div>
            </div>
          )}

          {/* Action Plan */}
          {output.action_plan && (
            <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-200">
              <h4 className="font-semibold mb-3 text-emerald-800">Action Plan</h4>
              <div className="space-y-3">
                <div><p className="text-xs font-semibold text-emerald-700 mb-1">Immediate Steps</p>
                  <ul className="space-y-1">{output.action_plan.immediate_steps?.map((s, i) => <li key={i} className="text-sm text-emerald-900 flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />{s}</li>)}</ul>
                </div>
                <div><p className="text-xs font-semibold text-emerald-700 mb-1">30-Day Actions</p>
                  <ul className="space-y-1">{output.action_plan["30_day_actions"]?.map((s, i) => <li key={i} className="text-sm text-emerald-900 flex items-start gap-2"><Target className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" />{s}</li>)}</ul>
                </div>
                {output.action_plan["90_day_review"] && <div className="p-3 bg-white rounded-lg"><p className="text-xs font-semibold text-emerald-700 mb-1">90-Day Review</p><p className="text-sm text-emerald-900">{output.action_plan["90_day_review"]}</p></div>}
                {output.action_plan.pricing_sweet_spot && <div className="p-3 bg-white rounded-lg"><p className="text-xs font-semibold text-emerald-700 mb-1">Finding Your Sweet Spot</p><p className="text-sm text-emerald-900">{output.action_plan.pricing_sweet_spot}</p></div>}
              </div>
            </div>
          )}

          {/* Glossary */}
          {output.glossary && output.glossary.length > 0 && (
            <div className="p-5 rounded-xl bg-neutral-50 border border-neutral-200">
              <h4 className="font-semibold mb-3 text-neutral-800">Glossary of Terms</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {output.glossary.map((g, i) => (
                  <div key={i} className="p-3 bg-white rounded-lg border border-neutral-100">
                    <p className="font-semibold text-sm text-neutral-900">{g.term}</p>
                    <p className="text-xs text-neutral-600 mt-1">{g.definition}</p>
                    {g.formula && <p className="text-xs font-mono text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded">{g.formula}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <BrandedFooter />
        </div>
      );


    case "implementation_plan":
      if (output.raw_output && !output.weekly_plan) {
        let parsedOutput = output;
        try {
          if (typeof output.raw_output === 'string') {
            let cleanJson = output.raw_output.trim();
            if (cleanJson.startsWith('```')) {
              cleanJson = cleanJson.split('```')[1];
              if (cleanJson.startsWith('json')) cleanJson = cleanJson.slice(4);
            }
            const parsed = JSON.parse(cleanJson.trim());
            parsedOutput = { ...output, ...parsed };
          }
        } catch {
          return (
            <div>
              <BrandedHeader title="90-Day Implementation Plan" subtitle="Strategic execution roadmap" />
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-700">{output.raw_output}</div>
              <BrandedFooter />
            </div>
          );
        }
        output = parsedOutput;
      }

      return (
        <div className="space-y-6">
          <BrandedHeader title={output.plan_title || "90-Day Implementation Plan"} subtitle="Strategic execution roadmap co-developed with My Biz Consulting" />

          {output.executive_summary && (
            <div className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border-l-4 border-emerald-500">
              <p className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-1">Executive Summary</p>
              <p className="text-gray-700 leading-relaxed">{output.executive_summary}</p>
            </div>
          )}

          {output.ceo_constraints && (
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
              <h4 className="font-semibold mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-gray-600" /> CEO Constraints</h4>
              <p className="text-sm">Max Hours/Week: <strong>{output.ceo_constraints.max_hours_per_week}</strong></p>
              {output.ceo_constraints.off_limits_tasks?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {output.ceo_constraints.off_limits_tasks.map((task, i) => (
                    <Badge key={i} className="bg-red-100 text-red-700 border-red-200">{task}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {output.weekly_plan && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-600" /> Weekly Execution Plan</h4>
              <div className="relative">
                <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-emerald-400 to-green-200" />
                <div className="space-y-4">
                  {output.weekly_plan.map((week, i) => (
                    <div key={i} className="flex gap-4 relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center text-xs font-bold z-10 flex-shrink-0 shadow-md">
                        W{week.week}
                      </div>
                      <div className="flex-1 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-green-50 font-semibold text-emerald-800 text-sm">{week.theme}</div>
                        <div className="p-4 bg-white space-y-3">
                          {week.outcomes?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 mb-1">Outcomes:</p>
                              <ul className="text-sm space-y-1">{week.outcomes.map((o, j) => <li key={j} className="flex items-start gap-1"><CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />{o}</li>)}</ul>
                            </div>
                          )}
                          {week.tasks?.length > 0 && (
                            <div className="space-y-2">
                              {week.tasks.map((task, j) => (
                                <div key={j} className="p-2 bg-gray-50 rounded-lg text-sm flex items-center justify-between border border-gray-100">
                                  <span>{task.task}</span>
                                  <div className="flex gap-2">
                                    <Badge variant="outline" className="text-xs">{task.owner}</Badge>
                                    <Badge className={PRIORITY_COLORS[task.priority]?.badge || "bg-gray-100 text-gray-600"}>{task.priority}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <BrandedFooter />
        </div>
      );

    default:
      return (
        <div>
          <BrandedHeader title="Generated Output" />
          <div className="prose prose-sm max-w-none">{renderPlainTextOutput(output)}</div>
          <BrandedFooter />
        </div>
      );
  }
};

// Visual Org Chart Component
const OrgChartVisual = ({ roles }) => {
  const topRole = roles.find(r => !r.reports_to || r.reports_to === "N/A" || r.reports_to === "None" || r.reports_to === "Board");
  const getReports = (roleName) => roles.filter(r => r.reports_to === roleName);
  
  const RoleNode = ({ role, depth = 0 }) => {
    const reports = getReports(role.role);
    const statusColor = role.status === "Filled" ? "border-emerald-400 bg-emerald-50" : role.status === "Gap" ? "border-red-400 bg-red-50" : "border-amber-400 bg-amber-50";
    const statusBadge = role.status === "Filled" ? "bg-emerald-100 text-emerald-700" : role.status === "Gap" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
    
    return (
      <div className="flex flex-col items-center">
        <div className={`px-4 py-3 rounded-xl border-2 ${statusColor} shadow-sm min-w-[140px] text-center`}>
          <p className="font-semibold text-sm">{role.role}</p>
          {role.current_owner && <p className="text-xs text-gray-500">{role.current_owner}</p>}
          <Badge className={`${statusBadge} text-[10px] mt-1`}>{role.status}</Badge>
        </div>
        {reports.length > 0 && (
          <>
            <div className="w-0.5 h-4 bg-gray-300" />
            <div className="flex gap-3 flex-wrap justify-center">
              {reports.map((r, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-0.5 h-4 bg-gray-300" />
                  <RoleNode role={r} depth={depth + 1} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  if (!topRole) {
    return (
      <div className="grid md:grid-cols-2 gap-3">
        {roles.map((role, i) => {
          const statusColor = role.status === "Filled" ? "border-emerald-300 bg-emerald-50" : role.status === "Gap" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50";
          return (
            <div key={i} className={`p-4 rounded-xl border-2 ${statusColor}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" />{role.role}</span>
                <Badge variant={role.status === "Filled" ? "default" : "destructive"}>{role.status}</Badge>
              </div>
              {role.reports_to && <p className="text-xs text-gray-500">Reports to: {role.reports_to}</p>}
              {role.current_owner && <p className="text-xs text-gray-500">Current: {role.current_owner}</p>}
              {role.key_responsibilities?.length > 0 && (
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  {role.key_responsibilities.map((resp, j) => <li key={j}>&#8226; {resp}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto py-4">
      <div className="flex justify-center min-w-[400px]">
        <RoleNode role={topRole} />
      </div>
    </div>
  );
};

// Helper function to render output as plain text instead of code/JSON
const renderPlainTextOutput = (obj, level = 0) => {
  if (!obj || typeof obj !== 'object') {
    return <span>{String(obj)}</span>;
  }

  const entries = Object.entries(obj).filter(([key]) => 
    !['output_id', 'user_id', 'tool', 'generated_at', '_id'].includes(key)
  );

  return (
    <div className={level > 0 ? "ml-4 mt-2" : ""}>
      {entries.map(([key, value], index) => {
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        
        if (Array.isArray(value)) {
          return (
            <div key={index} className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">{formattedKey}</h4>
              <ul className="space-y-1">
                {value.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    {typeof item === 'object' ? (
                      <div className="p-2 bg-gray-50 rounded mb-2">
                        {renderPlainTextOutput(item, level + 1)}
                      </div>
                    ) : (
                      <span>• {item}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        } else if (typeof value === 'object' && value !== null) {
          return (
            <div key={index} className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">{formattedKey}</h4>
              {renderPlainTextOutput(value, level + 1)}
            </div>
          );
        } else {
          return (
            <div key={index} className="mb-2 text-sm">
              <span className="text-gray-500">{formattedKey}:</span>{' '}
              <span className="text-gray-900">{String(value)}</span>
            </div>
          );
        }
      })}
    </div>
  );
};

// SOP Tab Component
const SOPTab = ({ sops, onShowGenerator, onDeleteSop, onUpdateSop, profile }) => {
  const [selectedSop, setSelectedSop] = useState(null);
  const [editingSop, setEditingSop] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  const startEdit = (sop, e) => {
    e.stopPropagation();
    setEditForm({ sop_title: sop.sop_title || "", purpose: sop.purpose || "", scope: sop.scope || "", owner: sop.owner || "" });
    setEditingSop(sop);
  };

  const saveEdit = async () => {
    if (!editingSop) return;
    const ok = await onUpdateSop(editingSop.output_id, editForm);
    if (ok) { setEditingSop(null); setSelectedSop(null); }
  };

  const handleDelete = (sop, e) => {
    e.stopPropagation();
    setConfirmDelete(sop);
  };

  const confirmDeleteSop = () => {
    if (confirmDelete) {
      onDeleteSop(confirmDelete.output_id);
      setConfirmDelete(null);
      if (selectedSop?.output_id === confirmDelete.output_id) setSelectedSop(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SOP Generator</h1>
          <p className="text-gray-500">Document your key processes so they run without you</p>
        </div>
        <Button onClick={onShowGenerator} className="bg-green-600 hover:bg-green-700" data-testid="create-sop-btn">
          <FileText className="w-4 h-4 mr-2" /> Create New SOP
        </Button>
      </div>

      {sops.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {sops.map((sop) => (
            <Card key={sop.output_id} className="group cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedSop(sop)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-medium flex-1">{sop.sop_title || sop.process_name}</h3>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => startEdit(sop, e)} data-testid={`edit-sop-${sop.output_id}`}>
                      <Pencil className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:text-red-600" onClick={(e) => handleDelete(sop, e)} data-testid={`delete-sop-${sop.output_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">{sop.purpose || "Click to view"}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-gray-400">{sop.steps?.length || 0} steps</span>
                  <span className="text-xs text-gray-400">{new Date(sop.generated_at).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-medium text-gray-900">No SOPs yet</h3>
            <p className="text-sm text-gray-500 mt-1">Create your first Standard Operating Procedure</p>
            <Button onClick={onShowGenerator} className="mt-4 bg-green-600 hover:bg-green-700">Create First SOP</Button>
          </CardContent>
        </Card>
      )}

      {/* SOP Detail Dialog */}
      <Dialog open={!!selectedSop && !editingSop} onOpenChange={() => setSelectedSop(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedSop && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedSop.sop_title}</DialogTitle>
                <DialogDescription>{selectedSop.purpose}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {selectedSop.scope && (
                  <div><Label className="text-xs text-gray-500">Scope</Label><p className="text-sm">{selectedSop.scope}</p></div>
                )}
                {selectedSop.owner && (
                  <div><Label className="text-xs text-gray-500">Owner</Label><p className="text-sm">{selectedSop.owner}</p></div>
                )}
                {selectedSop.tools_needed && (
                  <div>
                    <Label className="text-xs text-gray-500">Tools Needed</Label>
                    <div className="flex flex-wrap gap-1 mt-1">{selectedSop.tools_needed.map((t, i) => <Badge key={i} variant="outline">{t}</Badge>)}</div>
                  </div>
                )}
                {selectedSop.steps && (
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Steps</Label>
                    <div className="space-y-3">
                      {selectedSop.steps.map((step, i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium flex-shrink-0">{step.step_number}</span>
                            <div>
                              <p className="font-medium text-sm">{step.title}</p>
                              <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                              {step.time_estimate && <p className="text-xs text-gray-400 mt-1">Time: {step.time_estimate}</p>}
                              {step.tips && <p className="text-xs text-blue-600 mt-1">Tip: {step.tips}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(selectedSop, { stopPropagation: () => {} })}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setConfirmDelete(selectedSop); }}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(JSON.stringify(selectedSop, null, 2))}>
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button size="sm" onClick={() => setSelectedSop(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit SOP Dialog */}
      <Dialog open={!!editingSop} onOpenChange={() => setEditingSop(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit SOP</DialogTitle>
            <DialogDescription>Update the SOP details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title</Label>
              <Input value={editForm.sop_title} onChange={e => setEditForm(p => ({...p, sop_title: e.target.value}))} />
            </div>
            <div>
              <Label>Purpose</Label>
              <Textarea value={editForm.purpose} onChange={e => setEditForm(p => ({...p, purpose: e.target.value}))} rows={2} />
            </div>
            <div>
              <Label>Scope</Label>
              <Input value={editForm.scope} onChange={e => setEditForm(p => ({...p, scope: e.target.value}))} />
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={editForm.owner} onChange={e => setEditForm(p => ({...p, owner: e.target.value}))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSop(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-green-600 hover:bg-green-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete SOP</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{confirmDelete?.sop_title}"? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDeleteSop} data-testid="confirm-delete-sop">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Onboarding Flow Component
const OnboardingFlow = ({ step, setStep, form, setForm, onSave, onClose, isDialog }) => {
  const totalSteps = 4;
  
  const timeDrainOptions = [
    "Email management", "Client calls", "Sales/prospecting", "Operations/admin",
    "Team management", "Fires/emergencies", "Meetings", "Social media", "Invoicing/billing"
  ];
  
  const challengeOptions = [
    "Finding time for strategic work", "Delegating effectively", "Hiring the right people",
    "Consistent lead flow", "Cash flow management", "Work-life balance", "Team accountability"
  ];
  
  const painPointOptions = [
    { id: "nothing_runs_without_me", label: "Nothing runs without me" },
    { id: "cant_take_vacation", label: "Can't take a real vacation" },
    { id: "team_needs_constant_direction", label: "Team needs constant direction" },
    { id: "stuck_in_weeds", label: "Stuck in day-to-day operations" },
    { id: "no_clear_processes", label: "No clear processes documented" },
    { id: "inconsistent_execution", label: "Inconsistent team execution" }
  ];

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  return (
    <div className={isDialog ? "" : "max-w-2xl mx-auto"}>
      {!isDialog && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Complete Your Business Profile</h1>
          <p className="text-gray-500">This helps us personalize your scaling tools and recommendations.</p>
        </div>
      )}

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>Step {step} of {totalSteps}</span>
          <span>{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <Progress value={(step / totalSteps) * 100} className="h-2" />
      </div>

      {/* Step 1: Business Basics */}
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-green-600" />
            Business Basics
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Business Name</Label>
              <Input
                value={form.business_name}
                onChange={(e) => updateForm("business_name", e.target.value)}
                placeholder="Your business name"
              />
            </div>
            <div>
              <Label>Industry</Label>
              <Input
                value={form.industry}
                onChange={(e) => updateForm("industry", e.target.value)}
                placeholder="e.g., Marketing, Consulting, E-commerce"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Business Type</Label>
              <Select value={form.business_type} onValueChange={(v) => updateForm("business_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service-based</SelectItem>
                  <SelectItem value="product">Product-based</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Years in Business</Label>
              <Select value={String(form.years_in_business)} onValueChange={(v) => updateForm("years_in_business", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Less than 1 year</SelectItem>
                  <SelectItem value="1">1-2 years</SelectItem>
                  <SelectItem value="3">3-5 years</SelectItem>
                  <SelectItem value="6">6-10 years</SelectItem>
                  <SelectItem value="10">10+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Revenue Range</Label>
              <Select value={form.revenue_range} onValueChange={(v) => updateForm("revenue_range", v)}>
                <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<100k">Under $100K</SelectItem>
                  <SelectItem value="100k-500k">$100K - $500K</SelectItem>
                  <SelectItem value="500k-1M">$500K - $1M</SelectItem>
                  <SelectItem value="1M-5M">$1M - $5M</SelectItem>
                  <SelectItem value="5M+">$5M+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team Size (including you)</Label>
              <Select value={String(form.employee_count)} onValueChange={(v) => updateForm("employee_count", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Just me</SelectItem>
                  <SelectItem value="2">2-5</SelectItem>
                  <SelectItem value="6">6-10</SelectItem>
                  <SelectItem value="11">11-25</SelectItem>
                  <SelectItem value="26">26+</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Time & Focus */}
      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Timer className="w-5 h-5 text-green-600" />
            Time & Focus
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Current hours you work per week</Label>
              <Select value={String(form.ceo_hours_per_week)} onValueChange={(v) => updateForm("ceo_hours_per_week", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">~30 hours</SelectItem>
                  <SelectItem value="40">~40 hours</SelectItem>
                  <SelectItem value="50">~50 hours</SelectItem>
                  <SelectItem value="60">~60 hours</SelectItem>
                  <SelectItem value="70">70+ hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ideal hours you want to work</Label>
              <Select value={String(form.ideal_ceo_hours)} onValueChange={(v) => updateForm("ideal_ceo_hours", parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">~20 hours</SelectItem>
                  <SelectItem value="25">~25 hours</SelectItem>
                  <SelectItem value="30">~30 hours</SelectItem>
                  <SelectItem value="40">~40 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Where does your time go? (Select all that apply)</Label>
            <div className="flex flex-wrap gap-2">
              {timeDrainOptions.map((option) => (
                <Badge
                  key={option}
                  variant={form.top_time_drains.includes(option) ? "default" : "outline"}
                  className={`cursor-pointer ${form.top_time_drains.includes(option) ? "bg-green-600" : ""}`}
                  onClick={() => toggleArrayItem("top_time_drains", option)}
                >
                  {option}
                </Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Biggest challenges right now</Label>
            <div className="flex flex-wrap gap-2">
              {challengeOptions.map((option) => (
                <Badge
                  key={option}
                  variant={form.biggest_challenges.includes(option) ? "default" : "outline"}
                  className={`cursor-pointer ${form.biggest_challenges.includes(option) ? "bg-green-600" : ""}`}
                  onClick={() => toggleArrayItem("biggest_challenges", option)}
                >
                  {option}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Operations */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-green-600" />
            Operations Maturity
          </h3>

          <div>
            <Label>How would you rate your operations maturity? (1-5)</Label>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <Button
                  key={n}
                  variant={form.ops_maturity === n ? "default" : "outline"}
                  className={form.ops_maturity === n ? "bg-green-600" : ""}
                  onClick={() => updateForm("ops_maturity", n)}
                >
                  {n}
                </Button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">1 = Chaos, everything is ad-hoc • 5 = Systems run without me</p>
          </div>

          <div className="space-y-3">
            <Label>What do you currently have in place?</Label>
            {[
              { field: "has_sops", label: "Documented SOPs / processes" },
              { field: "has_kpis", label: "KPIs / metrics tracking" },
              { field: "has_org_chart", label: "Clear org chart / roles" },
              { field: "has_meeting_rhythm", label: "Regular team meetings / cadence" }
            ].map((item) => (
              <div key={item.field} className="flex items-center space-x-2">
                <Checkbox
                  id={item.field}
                  checked={form[item.field]}
                  onCheckedChange={(checked) => updateForm(item.field, checked)}
                />
                <label htmlFor={item.field} className="text-sm cursor-pointer">{item.label}</label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 4: Goals */}
      {step === 4 && (
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Goals & Pain Points
          </h3>

          <div>
            <Label>What's your primary goal?</Label>
            <Select value={form.primary_goal} onValueChange={(v) => updateForm("primary_goal", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="scale">Scale the business</SelectItem>
                <SelectItem value="freedom">More freedom / less hours</SelectItem>
                <SelectItem value="profit">Increase profitability</SelectItem>
                <SelectItem value="exit">Prepare for exit / sale</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>How urgently do you need change?</Label>
            <Select value={form.timeline_urgency} onValueChange={(v) => updateForm("timeline_urgency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Within 30 days</SelectItem>
                <SelectItem value="60">Within 60 days</SelectItem>
                <SelectItem value="90">Within 90 days</SelectItem>
                <SelectItem value="180">6 months+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-2 block">Which of these resonate? (Select all that apply)</Label>
            <div className="space-y-2">
              {painPointOptions.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={option.id}
                    checked={form.pain_points.includes(option.id)}
                    onCheckedChange={() => toggleArrayItem("pain_points", option.id)}
                  />
                  <label htmlFor={option.id} className="text-sm cursor-pointer">{option.label}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-4 border-t">
        <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : onClose()}>
          {step === 1 ? "Cancel" : "Back"}
        </Button>
        {step < totalSteps ? (
          <Button onClick={() => setStep(step + 1)} className="bg-green-600 hover:bg-green-700">
            Continue <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={onSave} className="bg-green-600 hover:bg-green-700">
            Save & Start Scaling
          </Button>
        )}
      </div>
    </div>
  );
};

export default ScalingDashboard;
